import { defineStore } from "pinia";

import walletService from "../../service/wallets/wallet.service";
import { Transfer } from "../../service/wallets/interface/wallet.interface";

interface State {}
const walletStore = defineStore("wallet", {
  state: () => ({
    // the main store to keep the unedited data
    chartInfo: {
      name: [""],
      rate: [0],
    },

    // released to the user
    updatedChartInfo: {
      name: [""],
      rate: [0],
      taxRate: [0] as number[],
      pensionRate: [0] as number[],
    },

    totalAmount: 0,
    years: [],
  }),
  getters: {
    getYear(): any {},
    chartData(): any {
      return {
        labels: [
          "Jan",
          "Feb",
          "Mar",
          "Apr",
          "May",
          "Jun",
          "Jul",
          "Aug",
          "Sep",
          "Oct",
          "Nov",
          "Dec",
        ],
        datasets: [
          {
            label: "Salary",
            data: this.updatedChartInfo.rate,
            borderColor: "#0D2D22",
            backgroundColor: "rgba(13, 45, 34, 0.18)",
            pointBackgroundColor: "#0D2D22",
            pointBorderColor: "#ffffff",
            fill: "origin",
          },
          {
            label: "Tax",
            data: this.updatedChartInfo.taxRate,
            borderColor: "#7AAB78",
            backgroundColor: "rgba(122, 171, 120, 0.18)",
            pointBackgroundColor: "#7AAB78",
            pointBorderColor: "#ffffff",
            fill: "origin",
          },
          {
            label: "Pension",
            data: this.updatedChartInfo.pensionRate,
            borderColor: "#FFB400",
            backgroundColor: "rgba(255, 180, 0, 0.18)",
            pointBackgroundColor: "#FFB400",
            pointBorderColor: "#ffffff",
            fill: "origin",
          },
        ],
      };
    },
    chartOptions(): any {
      return {
        elements: {
          line: { tension: 0.35, borderWidth: 2.5, fill: true },
          point: { radius: 3, hoverRadius: 5, borderWidth: 2 },
        },
        plugins: {
          legend: {
            display: true,
            position: "top" as const,
            align: "end" as const,
            labels: {
              usePointStyle: true,
              pointStyle: "circle",
              padding: 20,
            },
          },
          tooltip: {
            callbacks: {
              label(context: any) {
                return `${context.dataset.label}: ₦${context.formattedValue}`;
              },
            },
          },
        },
        responsive: true,
        scales: {
          x: {
            grid: {
              display: false,
            },
            ticks: {
              color: "rgba(34, 34, 34, 0.38)",
            },
          },
          y: {
            grid: {
              color: "rgba(34, 34, 34, 0.12)",
              lineWidth: 1,
            },
            border: {
              dash: [8, 10],
              dashOffset: 4,
              color: "rgba(34, 34, 34, 0.12)",
            },
            ticks: {
              callback: function (value: string) {
                return "₦ " + value;
              },
              color: "rgba(34, 34, 34, 0.38)",
            },
          },
        },
      };
    },
  },
  actions: {
    async getWallet(): Promise<any> {
      try {
        const response = await walletService.getWallet();
        if (response.data) {
          return await Promise.resolve(response);
        } else if (response.response) {
          return await Promise.reject(response.response);
        } else {
          return await Promise.reject(response.message);
        }
      } catch (error: any) {
        return await Promise.reject(error);
      }
    },

    async getTotalRevenue(organisationId?: number): Promise<any> {
      try {
        const response = await walletService.getTotalRevenue(organisationId);
        if (response.data) {
          return await Promise.resolve(response);
        } else if (response.response) {
          return await Promise.reject(response.response);
        } else {
          return await Promise.reject(response.message);
        }
      } catch (error: any) {
        return await Promise.reject(error);
      }
    },

    async pendingDisbursement(organisationId?: number): Promise<any> {
      try {
        const response =
          await walletService.pendingDisbursement(organisationId);
        if (response.data) {
          return await Promise.resolve(response);
        } else if (response.response) {
          return await Promise.reject(response.response);
        } else {
          return await Promise.reject(response.message);
        }
      } catch (error: any) {
        return await Promise.reject(error);
      }
    },
    async getTotalFundReceived(organisationId?: number): Promise<any> {
      try {
        const response =
          await walletService.getTotalFundReceived(organisationId);
        if (response.data) {
          return await Promise.resolve(response);
        } else if (response.response) {
          return await Promise.reject(response.response);
        } else {
          return await Promise.reject(response.message);
        }
      } catch (error: any) {
        return await Promise.reject(error);
      }
    },
    async getTotalFundDisbursed(organisationId?: number): Promise<any> {
      try {
        const response =
          await walletService.getTotalFundDisbursed(organisationId);
        if (response.data) {
          return await Promise.resolve(response);
        } else if (response.response) {
          return await Promise.reject(response.response);
        } else {
          return await Promise.reject(response.message);
        }
      } catch (error: any) {
        return await Promise.reject(error);
      }
    },

    async getBanks(): Promise<any> {
      try {
        const response = await walletService.getBanks();
        if (response.data) {
          return await Promise.resolve(response);
        } else if (response.response) {
          return await Promise.reject(response.response);
        } else {
          return await Promise.reject(response.message);
        }
      } catch (error: any) {
        return await Promise.reject(error);
      }
    },
    async transfer(data: Transfer): Promise<any> {
      try {
        const response = await walletService.transfer(data);
        if (response.data) {
          return await Promise.resolve(response);
        } else if (response.response) {
          return await Promise.reject(response.response);
        } else {
          return await Promise.reject(response.message);
        }
      } catch (error: any) {
        return await Promise.reject(error);
      }
    },
    async create(): Promise<any> {
      try {
        const response = await walletService.createWallet();
        if (response.data) {
          return await Promise.resolve(response);
        } else if (response.response) {
          return await Promise.reject(response.response);
        } else {
          return await Promise.reject(response.message);
        }
      } catch (error: any) {
        return await Promise.reject(error);
      }
    },

    async getWalletHistory(): Promise<any> {
      try {
        const response = await walletService.getWalletHistory();
        if (response.data) {
          return await Promise.resolve(response);
        } else if (response.response) {
          return await Promise.reject(response.response);
        } else {
          return await Promise.reject(response.message);
        }
      } catch (error: any) {
        return await Promise.reject(error);
      }
    },

    async getChartYears(): Promise<any> {
      try {
        const response = await walletService.getChartYears();
        if (response.data) {
          return await Promise.resolve(response);
        } else if (response.response) {
          return await Promise.reject(response.response);
        } else {
          return await Promise.reject(response.message);
        }
      } catch (error: any) {
        return await Promise.reject(error);
      }
    },

    async getChartDetail(
      chartYear: number,
      organisationId?: number,
    ): Promise<any> {
      try {
        const response = await walletService.getChartData(
          chartYear,
          organisationId,
        );
        if (response && response.data) {
          return await Promise.resolve(response);
        } else if (response && response.response) {
          return await Promise.reject(response.response);
        } else {
          return await Promise.reject(
            response?.message || "Failed to fetch chart data",
          );
        }
      } catch (error: any) {
        return await Promise.reject(error);
      }
    },

    async getLastTransaction(): Promise<any> {
      try {
        const response = await walletService.getLastTransaction();
        if (response && response.data) {
          return await Promise.resolve(response);
        } else if (response && response.response) {
          return await Promise.reject(response.response);
        } else {
          return await Promise.reject(
            response?.message || "Failed to fetch last transaction",
          );
        }
      } catch (error: any) {
        return await Promise.reject(error);
      }
    },

    async getTotalWalletBalance(): Promise<any> {
      try {
        const response = await walletService.getTotalWalletBalance();
        if (response && response.data) {
          return await Promise.resolve(response);
        } else if (response && response.response) {
          return await Promise.reject(response.response);
        } else {
          return await Promise.reject(
            response?.message || "Failed to fetch wallet balance",
          );
        }
      } catch (error: any) {
        return await Promise.reject(error);
      }
    },

    async getDashboardFundsSummary(params?: {
      startDate?: string;
      endDate?: string;
      organisationId?: number;
    }): Promise<any> {
      try {
        const response = await walletService.getDashboardFundsSummary(params);
        if (response && response.data) {
          return await Promise.resolve(response);
        } else if (response && response.response) {
          return await Promise.reject(response.response);
        } else {
          return await Promise.reject(
            response?.message || "Failed to fetch funds summary",
          );
        }
      } catch (error: any) {
        return await Promise.reject(error);
      }
    },

    async initChart(data: any): Promise<any> {
      if (Array.isArray(data)) {
        // New format: array of { month, salary, tax, pension, health }
        this.chartInfo.rate = data.map((item: any) => item.salary || 0);
        this.updatedChartInfo.rate = this.chartInfo.rate;
        this.updatedChartInfo.taxRate = data.map((item: any) => item.tax || 0);
        this.updatedChartInfo.pensionRate = data.map(
          (item: any) => item.pension || 0,
        );
        this.totalAmount = data.reduce(
          (sum: number, item: any) =>
            sum +
            (item.salary || 0) +
            (item.tax || 0) +
            (item.pension || 0) +
            (item.health || 0),
          0,
        );
      } else {
        // Legacy format: object with month keys
        this.chartInfo.rate = Object.values(data);
        this.updatedChartInfo.rate = this.chartInfo.rate;
        this.totalAmount = 0;
        for (let key in data) {
          this.totalAmount += data[key];
        }
      }
    },
    async updateChart(data: any): Promise<any> {
      if (Array.isArray(data)) {
        this.chartInfo.rate = data.map((item: any) => item.salary || 0);
        this.updatedChartInfo.rate = this.chartInfo.rate;
        this.updatedChartInfo.taxRate = data.map((item: any) => item.tax || 0);
        this.updatedChartInfo.pensionRate = data.map(
          (item: any) => item.pension || 0,
        );
        this.totalAmount = data.reduce(
          (sum: number, item: any) =>
            sum +
            (item.salary || 0) +
            (item.tax || 0) +
            (item.pension || 0) +
            (item.health || 0),
          0,
        );
      } else {
        this.chartInfo.rate = Object.values(data);
        this.updatedChartInfo.rate = this.chartInfo.rate;
        this.totalAmount = 0;
        for (let key in data) {
          this.totalAmount += data[key];
        }
      }
    },
  },
});

export default walletStore;
